// ABOUTME: Reusable PWA install prompt component with platform-specific UI
// ABOUTME: Supports page, modal, and inline variants for use in onboarding, banners, and settings

import { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import "./InstallPrompt.scss";

// Types for platform detection
type Platform = "ios" | "android" | "other";
type InstallState = "available" | "installed" | "unsupported";

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

export interface InstallPromptProps {
  variant: "page" | "modal" | "inline";
  onComplete?: () => void;
  onSkip?: () => void;
  showSkip?: boolean;
}

// Platform detection utilities
function detectPlatform(): Platform {
  const ua = navigator.userAgent.toLowerCase();
  const isIOS = /iphone|ipad|ipod/.test(ua);
  const isAndroid = /android/.test(ua);

  if (isIOS) return "ios";
  if (isAndroid) return "android";
  return "other";
}

function detectInstallState(): InstallState {
  // Check if running in standalone mode (already installed)
  const isStandalone =
    window.matchMedia("(display-mode: standalone)").matches ||
    (window.navigator as any).standalone === true;

  if (isStandalone) return "installed";

  const platform = detectPlatform();

  // iOS Safari can always show manual instructions
  if (platform === "ios") {
    // Check if it's Safari (not Chrome or other browsers on iOS)
    const isSafari =
      /safari/.test(navigator.userAgent.toLowerCase()) &&
      !/crios|fxios/.test(navigator.userAgent.toLowerCase());
    return isSafari ? "available" : "unsupported";
  }

  // Android - we'll update this when beforeinstallprompt fires
  return "available";
}

function isInSafari(): boolean {
  const ua = navigator.userAgent.toLowerCase();
  return (
    /safari/.test(ua) && !/chrome/.test(ua) && !/crios|fxios|edgios/.test(ua)
  );
}

// iOS Share icon component
function ShareIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
      <polyline points="16 6 12 2 8 6" />
      <line x1="12" y1="2" x2="12" y2="15" />
    </svg>
  );
}

// Plus icon for Add to Home Screen
function PlusSquareIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
      <line x1="12" y1="8" x2="12" y2="16" />
      <line x1="8" y1="12" x2="16" y2="12" />
    </svg>
  );
}

// Checkmark icon
function CheckIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

// App icon for home screen
function AppIcon() {
  return (
    <img
      src="/icon-192.png"
      alt="Touchstone"
      className="install-prompt__app-icon"
    />
  );
}

export function InstallPrompt({
  variant,
  onComplete,
  onSkip,
  showSkip = true,
}: InstallPromptProps) {
  const [platform] = useState<Platform>(() => {
    const p = detectPlatform();
    console.log("[InstallPrompt] Platform detected:", p);
    return p;
  });
  const [installState, setInstallState] = useState<InstallState>(() => {
    const state = detectInstallState();
    console.log("[InstallPrompt] Initial install state:", state);
    return state;
  });
  const [deferredPrompt, setDeferredPrompt] =
    useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalling, setIsInstalling] = useState(false);

  // Listen for beforeinstallprompt event (Android) and check global deferred prompt
  useEffect(() => {
    // Check if there's already a deferred prompt from main.tsx
    if ((window as any).deferredInstallPrompt) {
      console.log("[InstallPrompt] Found existing deferred prompt");
      setDeferredPrompt((window as any).deferredInstallPrompt);
      setInstallState("available");
    } else {
      console.log("[InstallPrompt] No deferred prompt found yet");
    }

    const handler = (e: Event) => {
      e.preventDefault();
      console.log("[InstallPrompt] beforeinstallprompt event fired");
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setInstallState("available");
    };

    window.addEventListener("beforeinstallprompt", handler);

    // Listen for successful install
    const installHandler = () => {
      console.log("[InstallPrompt] App installed");
      setInstallState("installed");
      onComplete?.();
    };
    window.addEventListener("appinstalled", installHandler);

    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
      window.removeEventListener("appinstalled", installHandler);
    };
  }, [onComplete]);

  // Trigger native install prompt (Android)
  const triggerInstall = useCallback(async () => {
    if (!deferredPrompt) return;

    setIsInstalling(true);
    try {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;

      if (outcome === "accepted") {
        setInstallState("installed");
        onComplete?.();
      } else {
        // User dismissed - store in localStorage to avoid re-prompting
        localStorage.setItem("pwa-install-dismissed", Date.now().toString());
      }
    } catch (error) {
      console.error("Install prompt failed:", error);
    } finally {
      setIsInstalling(false);
      setDeferredPrompt(null);
    }
  }, [deferredPrompt, onComplete]);

  // Handle "Got it" for iOS (user acknowledges instructions)
  const handleIOSComplete = useCallback(() => {
    onComplete?.();
  }, [onComplete]);

  // Helper to render footer (for page variant)
  const renderFooter = (
    primaryAction: React.ReactNode,
    showSkipButton = true,
  ) => {
    if (variant !== "page") {
      return (
        <>
          {primaryAction}
          {showSkipButton && showSkip && (
            <button className="install-prompt__skip" onClick={onSkip}>
              skip for now
            </button>
          )}
        </>
      );
    }
    return (
      <div className="install-prompt__footer">
        {primaryAction}
        {showSkipButton && showSkip && (
          <button className="install-prompt__skip" onClick={onSkip}>
            skip for now
          </button>
        )}
      </div>
    );
  };

  // Already installed state
  if (installState === "installed") {
    return (
      <div className={`install-prompt install-prompt--${variant} installed`}>
        <div className="install-prompt__content">
          <div className="install-prompt__icon install-prompt__icon--success">
            <CheckIcon />
          </div>
          <h2 className="install-prompt__title">Already Installed</h2>
          <p className="install-prompt__subtitle">
            You're all set! The app is on your home screen.
          </p>
        </div>
        {renderFooter(
          <button className="install-prompt__button" onClick={onComplete}>
            Continue
          </button>,
          false,
        )}
      </div>
    );
  }

  // Unsupported browser state
  if (
    installState === "unsupported" ||
    (platform === "other" && !deferredPrompt)
  ) {
    return (
      <div className={`install-prompt install-prompt--${variant} unsupported`}>
        <div className="install-prompt__content">
          <div className="install-prompt__visual">
            <AppIcon />
          </div>
          <h2 className="install-prompt__title">Add to Home Screen</h2>
          <p className="install-prompt__subtitle">
            For the best experience, open this page in{" "}
            {platform === "ios" ? "Safari" : "Chrome"}.
          </p>
        </div>
        {renderFooter(
          <button className="install-prompt__button" onClick={onComplete}>
            Continue Anyway
          </button>,
        )}
      </div>
    );
  }

  // iOS Safari - show manual instructions
  if (platform === "ios" && isInSafari()) {
    return (
      <div className={`install-prompt install-prompt--${variant} ios`}>
        <div className="install-prompt__content">
          <div className="install-prompt__visual">
            <AppIcon />
          </div>
          <h2 className="install-prompt__title">Add to Home Screen</h2>
          <p className="install-prompt__subtitle">
            Get quick access and the best experience
          </p>

          <div className="install-prompt__steps">
            <motion.div
              className="install-prompt__step"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              <div className="install-prompt__step-number">1</div>
              <div className="install-prompt__step-content">
                <div className="install-prompt__step-icon">
                  <ShareIcon />
                </div>
                <span>
                  Tap the <strong>Share</strong> button
                </span>
              </div>
            </motion.div>

            <motion.div
              className="install-prompt__step"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <div className="install-prompt__step-number">2</div>
              <div className="install-prompt__step-content">
                <div className="install-prompt__step-icon">
                  <PlusSquareIcon />
                </div>
                <span>
                  Scroll and tap <strong>Add to Home Screen</strong>
                </span>
              </div>
            </motion.div>

            <motion.div
              className="install-prompt__step"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <div className="install-prompt__step-number">3</div>
              <div className="install-prompt__step-content">
                <span>
                  Tap <strong>Add</strong> to confirm
                </span>
              </div>
            </motion.div>
          </div>
        </div>

        {renderFooter(
          <button
            className="install-prompt__button"
            onClick={handleIOSComplete}
          >
            Got It
          </button>,
        )}
      </div>
    );
  }

  // iOS but not Safari - show browser switch message
  if (platform === "ios" && !isInSafari()) {
    return (
      <div className={`install-prompt install-prompt--${variant} ios-other`}>
        <div className="install-prompt__content">
          <div className="install-prompt__visual">
            <AppIcon />
          </div>
          <h2 className="install-prompt__title">Add to Home Screen</h2>
          <p className="install-prompt__subtitle">
            For the best experience, open this page in Safari to add to your
            home screen.
          </p>
        </div>
        {renderFooter(
          <button className="install-prompt__button" onClick={onComplete}>
            Continue Anyway
          </button>,
        )}
      </div>
    );
  }

  // Android / Desktop with install prompt available
  return (
    <div className={`install-prompt install-prompt--${variant} android`}>
      <div className="install-prompt__content">
        <div className="install-prompt__visual">
          <div className="install-prompt__app-icon">
            <img src="/icon-192.png" alt="App icon" />
          </div>
        </div>
        <h2 className="install-prompt__title">Add to Home Screen</h2>
        {!deferredPrompt && platform === "android" ? (
          <p className="install-prompt__subtitle">
            Open your browser menu and tap "Add to Home Screen" or "Install App"
          </p>
        ) : (
          <p className="install-prompt__subtitle">
            Get quick access and the best experience
          </p>
        )}
      </div>

      {renderFooter(
        !deferredPrompt && platform === "android" ? (
          <button className="install-prompt__button" onClick={onComplete}>
            Got It
          </button>
        ) : (
          <button
            className="install-prompt__button"
            onClick={triggerInstall}
            disabled={isInstalling || !deferredPrompt}
          >
            {isInstalling ? "Installing..." : "Install App"}
          </button>
        ),
      )}
    </div>
  );
}

// Modal wrapper for use outside of onboarding
export function InstallPromptModal({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        className="install-prompt-modal-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <motion.div
          className="install-prompt-modal"
          initial={{ opacity: 0, y: 20, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 20, scale: 0.95 }}
          onClick={(e) => e.stopPropagation()}
        >
          <button className="install-prompt-modal__close" onClick={onClose}>
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
          <InstallPrompt
            variant="modal"
            onComplete={onClose}
            onSkip={onClose}
            showSkip={false}
          />
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

// Hook for checking install state
export function useInstallState() {
  const [state, setState] = useState<InstallState>(detectInstallState);
  const [platform] = useState<Platform>(detectPlatform);

  useEffect(() => {
    const handler = () => {
      setState("installed");
    };
    window.addEventListener("appinstalled", handler);

    // Check standalone mode on mount
    const checkStandalone = () => {
      if (
        window.matchMedia("(display-mode: standalone)").matches ||
        (window.navigator as any).standalone === true
      ) {
        setState("installed");
      }
    };
    checkStandalone();

    return () => window.removeEventListener("appinstalled", handler);
  }, []);

  return { installState: state, platform };
}
